import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation, redirect } from 'react-router-dom';
import LoginScreen from './screens/login/login';
import IncidentMainScreen from './screens/incidents/incidentMain';
import ResetPasswordScreen from './screens/login/resetPassword';
import SetPasswordScreen from './screens/login/setPassword';
import IncidentScreen from './screens/incidents/incident';
import UsersScreen from './screens/users/users';
import CreateUsersScreen from './screens/users/createUser';
import EditUsersScreen from './screens/users/editUser';
import CompaniesScreen from './screens/companies/companies';
import CreateCompanyScreen from './screens/companies/createCompany';
import EditCompanyScreen from './screens/companies/editCompany';
import PublicIncidentScreen from './screens/publicIncident/publicIncident';
import CreateIncidentScreen from './screens/publicIncident/createIncident';
import ConsultIncidentScreen from './screens/publicIncident/consulIncident';
import CreateFeedbackScreen from './screens/publicIncident/createFeedback';
import NotificationScreen from './screens/notifications/notification';
import './styles/global.css';
import NormativeScreen from './screens/normative/normative';
import AccessDeniedScreen from './screens/accessDenied/accessDenied';
import SubscriptionScreen from './screens/subscription/subscription';
import FormationScreen from './screens/formation/formation';


function isPublicRoute(route) {
  const publicRoutePatterns = [
    /^\/login$/,
    /^\/reset-password(\/.+)?$/,
    /^\/public(\/.+)?$/,
    /^\/create-incident(\/.+)?$/,
    /^\/check-incident(\/.+)?$/,
    /^\/create-feedback(\/.+)?$/,
    /^\/access-denied(\/.+)?$/
  ];
  return publicRoutePatterns.some(pattern => pattern.test(route));
}


function ProtectedRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasNavigated = useRef(false);

  useEffect(() => {
    const fullUrl = window.location.href;
    const routeStart = fullUrl.substring(window.location.origin.length);

    if (routeStart.startsWith('/#') && !hasNavigated.current) {
      const hash = new URL(fullUrl).hash.substr(1);
      const params = new URLSearchParams(hash);
      const code = params.get('code');

      if (code) {
        sessionStorage.setItem('code', code);
      }
      navigate('/companies');
      hasNavigated.current = true;
    }
  }, [navigate]); // Only re-run if navigate changes

  // Handling the second condition related to '/'
  useEffect(() => {
    const currentRoute = location.pathname;

    if (currentRoute === '/' && !hasNavigated.current) {
      navigate('/login');
      hasNavigated.current = true;
    }
  }, [location, navigate]); // Only re-run if location or navigate changes

  useEffect(() => {

    const adminRoutes = [
      '/users',
      '/create-user',
      '/edit-user/',
      '/companies',
      '/create-company',
      '/edit-company/',
      '/legal',
      '/access-denied',
      '/public/',
      '/subscription',
      '/formation',
      '/create-incident/',
      '/create-feedback/',
      '/check-incident'
    ]

    const responsibleRoutes = [
      '/incident/',
      '/incidents',
      '/notifications',
      '/legal',
      '/access-denied',
      '/public/',
      '/formation',
      '/create-incident/',
      '/create-feedback/',
      '/check-incident'
    ]

    const currentRoute = location.pathname;
    const authInfoString = localStorage.getItem('authInfo');
    const authInfo = authInfoString ? JSON.parse(authInfoString) : {};
    const isAuthenticated = authInfo.isAuthenticated;
    const role = authInfo.rol;

    if (!isPublicRoute(currentRoute) && !isAuthenticated) {
      navigate('/login');
      return;
    }

    const isAccessibleForAdmin = role === 'admin';
    const isAccessibleForResponsible = role === 'responsible';
    const isAccessibleForAdminResponsible = role === 'adminResponsible';


    const isRouteAccessibleForRole = (routes) => {
      return routes.includes(currentRoute) || routes.some(base => currentRoute.startsWith(base));
    };

    if (isAccessibleForAdmin && !isRouteAccessibleForRole(adminRoutes)) {
      navigate('/access-denied');
      return;
    }

    if (isAccessibleForResponsible && !isRouteAccessibleForRole(responsibleRoutes)) {
      navigate('/access-denied');
      return;
    }



  }, [location, navigate]);

  return (
    <div className='background-global font-global'>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/reset-password/:id/:idRequest" element={<SetPasswordScreen />} />
        <Route path="/incidents" element={<IncidentMainScreen />} />
        <Route path="/incident/:id" element={<IncidentScreen />} />
        <Route path="/users" element={<UsersScreen />} />
        <Route path="/create-user" element={<CreateUsersScreen />} />
        <Route path="/edit-user/:id" element={<EditUsersScreen />} />
        <Route path="/companies" element={<CompaniesScreen />} />
        <Route path="/create-company" element={<CreateCompanyScreen />} />
        <Route path="/edit-company/:id" element={<EditCompanyScreen />} />
        <Route path="/public/:id" element={<PublicIncidentScreen />} />
        <Route path="/create-feedback/:id" element={<CreateFeedbackScreen />} />
        <Route path="/create-incident/:id" element={<CreateIncidentScreen />} />
        <Route path="/check-incident/:id" element={<ConsultIncidentScreen />} />
        <Route path="/legal" element={<NormativeScreen />} />
        <Route path="/notifications" element={<NotificationScreen />} />
        <Route path="/access-denied" element={<AccessDeniedScreen />} />
        <Route path="/subscription" element={<SubscriptionScreen />} />
        <Route path="/formation" element={<FormationScreen />} />
      </Routes>
    </div>

  );
}

function App() {
  return (
    <Router>
      <ProtectedRoutes />
    </Router>
  );
}

export default App;
